import { useState, useEffect, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { processNoteContent, NOTE_PROSE_CLASSES } from '../utils/noteContent';
import ContentInsertModal from './ContentInsertModal';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authAxios = {
  post: (url, data, config = {}) =>
    axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

function isImageFile(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext);
}

/** Pretty-print HTML with newlines and indentation for readability */
function prettyPrintHtml(htmlStr) {
  if (!htmlStr || !htmlStr.trim()) return htmlStr;
  const raw = htmlStr.trim();
  const parts = raw.replace(/>\s*</g, '>\n<').split('\n');
  let depth = 0;
  const selfClosing = /^<(area|base|br|col|embed|hr|img|input|link|meta|source|track|wbr)[\s>]/i;
  const indent = '  ';
  const result = [];
  for (let line of parts) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const isClose = trimmed.startsWith('</');
    const isOpen = trimmed.startsWith('<');
    const isSelfClose = isOpen && (/\s*\/\s*>$/.test(trimmed) || selfClosing.test(trimmed));
    const hasClosingTag = trimmed.includes('</');
    if (isClose) depth = Math.max(0, depth - 1);
    result.push(indent.repeat(depth) + trimmed);
    if (isOpen && !isClose && !isSelfClose && !hasClosingTag) depth++;
  }
  return result.join('\n');
}

function NoteContentEditorModal({ isOpen, onClose, content, onContentChange, onSave, title }) {
  const [localContent, setLocalContent] = useState(content || '');
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLocalContent(content || '');
    }
  }, [isOpen, content]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const insertHtmlAtCursor = (htmlStr) => {
    const view = editorRef.current?.view;
    if (view) {
      view.dispatch(view.state.replaceSelection(htmlStr));
      return;
    }
    setLocalContent((prev) => prev + htmlStr);
  };

  const uploadFileAndGetUrl = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authAxios.post(`${API_BASE_URL}/admin/content/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (response.data?.success && response.data?.data?.name) {
      return `/api/content/${response.data.data.name}`;
    }
    throw new Error(response.data?.error || 'Upload failed');
  };

  const insertAtCursor = (text) => {
    const view = editorRef.current?.view;
    if (view) {
      view.dispatch(view.state.replaceSelection(text));
    } else {
      setLocalContent((prev) => prev + text);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind !== 'file') continue;
      const file = items[i].getAsFile();
      if (!file) continue;
      e.preventDefault();
      e.stopPropagation();
      setUploading(true);
      uploadFileAndGetUrl(file)
        .then((url) => {
          if (isImageFile(file.name)) {
            insertAtCursor(`<img src="${url}" alt="" />`);
          } else {
            insertAtCursor(`<a href="${url}" download>${file.name}</a>`);
          }
        })
        .catch((err) => console.error('Upload on paste failed:', err))
        .finally(() => setUploading(false));
      return;
    }
  };

  const handleDrop = (e) => {
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    e.preventDefault();
    const file = files[0];
    setUploading(true);
    uploadFileAndGetUrl(file)
      .then((url) => {
        if (isImageFile(file.name)) {
          insertAtCursor(`<img src="${url}" alt="" />`);
        } else {
          insertAtCursor(`<a href="${url}" download>${file.name}</a>`);
        }
      })
      .catch((err) => console.error('Upload on drop failed:', err))
      .finally(() => setUploading(false));
  };

  const handleSave = async () => {
    if (onSave) {
      setSaving(true);
      try {
        const success = await onSave(localContent);
        if (success) onClose();
      } finally {
        setSaving(false);
      }
    } else {
      onContentChange(localContent);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
        <h2 className="text-lg font-semibold text-gray-900 truncate">
          {title ? `Note: ${title}` : 'Note content'}
        </h2>
        <div className="flex items-center gap-2">
          {uploading && (
            <span className="text-sm text-gray-500">Uploading...</span>
          )}
          <button
            type="button"
            onClick={() => setLocalContent((prev) => prettyPrintHtml(prev))}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200"
            title="Format HTML with indentation"
          >
            Format
          </button>
          <button
            type="button"
            onClick={() => setShowInsertModal(true)}
            className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
          >
            Insert content
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Two columns: CodeMirror | preview */}
      <div className="flex-1 flex min-h-0">
        <div
          className="flex-1 flex flex-col border-r border-gray-200 overflow-hidden"
          onPasteCapture={handlePaste}
          onDropCapture={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <div className="flex-1 overflow-hidden min-h-0 flex flex-col p-2">
            <CodeMirror
              ref={editorRef}
              value={localContent}
              height="100%"
              minHeight="400px"
              extensions={[html()]}
              onChange={setLocalContent}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: true,
                bracketMatching: true,
                closeBrackets: true,
                indentOnInput: true
              }}
              className="flex-1 overflow-auto text-sm"
            />
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
          <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Preview</p>
          <div
            className={`bg-white rounded-lg border border-gray-200 p-6 ${NOTE_PROSE_CLASSES}`}
            dangerouslySetInnerHTML={{ __html: processNoteContent(localContent || '') }}
          />
        </div>
      </div>

      <ContentInsertModal
        isOpen={showInsertModal}
        onClose={() => setShowInsertModal(false)}
        onInsertHtml={insertHtmlAtCursor}
      />
    </div>
  );
}

export default NoteContentEditorModal;
